import React from 'react';

type Props = {
  children: React.ReactNode;
  /** Mount after this many ms (fallback if idle not supported). Default: 0 */
  delay?: number;
  /** Prefer requestIdleCallback when available. Default: true */
  idle?: boolean;
};

export default function DeferMount({ children, delay = 0, idle = true }: Props) {
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    let t: number | null = null;
    let cancelled = false;

    const run = () => {
      if (!cancelled) setMounted(true);
    };

    // 2x rAF ensures we yield one frame before mounting
    const mountNextFrame = () => {
      requestAnimationFrame(() => requestAnimationFrame(run));
    };

    if (typeof window !== 'undefined') {
      const ric: any = (window as any).requestIdleCallback;
      if (idle && typeof ric === 'function') {
        ric(run, { timeout: Math.max(250, delay) });
      } else if (delay > 0) {
        t = window.setTimeout(run, delay);
      } else {
        mountNextFrame();
      }
    } else {
      run();
    }

    return () => {
      cancelled = true;
      if (t) clearTimeout(t);
    };
  }, [delay, idle]);

  return mounted ? <>{children}</> : null;
}
